import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUnit } from "effector-react";
import { ChatInput } from "../ChatInput";
import { $sightsStore } from "../../state/appState";
import type { AssistantSuggestion } from "../../types/assistant";
import type { AssistantMessageBlock } from "../../types/assistant";
import type { Photo, Video } from "../../types";
import { requestSpeechToText } from "../../services/assistantClient";
import { useAudioController } from "../../hooks/useAudioController";
import { useMobileKeyboardOpen } from "../../hooks/useMobileKeyboardOpen";
import {
  AssistantChatPanel,
  type AssistantChatMode,
} from "../AssistantChatPanel";
import { Suggestions } from "../Suggestions";
import cx from "classnames";
import { LoadingText } from "../LoadingText";
import { AssistantMessage } from "../AssistantMessage";
import { PhotoGallery } from "../PhotoGallery";
import { PhotoViewer } from "../PhotoViewer";
import {
  $assistantFilteredSuggestions,
  $assistantIsBusy,
  $assistantGalleryItems,
  $assistantGalleryOpen,
  $assistantMessages,
  $assistantStatus,
  $assistantThinkingText,
  $assistantViewerIndex,
  $assistantViewerItems,
  $assistantViewerOpen,
  assistantExternalMessageRequested,
  assistantGalleryClosed,
  assistantGalleryOpened,
  assistantPromptSubmitted,
  assistantSuggestionSelected,
  assistantTextAnimationCompleted,
  assistantViewerClosed,
  assistantViewerIndexChanged,
  assistantViewerOpened,
} from "../../state/assistantHud";
import { $audioConfig } from "../../state/appConfig";
import { useI18n } from "../../i18n";
import { $locale } from "../../state/locale";
import { LOCALE_LANGUAGE_CODES } from "../../../server/src/shared/locale";

import styles from "./AssistantHud.module.css";

const getAssistantText = (blocks: AssistantMessageBlock[]) =>
  blocks
    .filter(
      (block): block is Extract<AssistantMessageBlock, { type: "paragraph" }> =>
        block.type === "paragraph"
    )
    .map((block) => block.text.trim())
    .filter(Boolean)
    .join("\n");

export function AssistantHud() {
  const {
    sights,
    messages,
    status,
    isBusy,
    thinkingText,
    filteredSuggestions,
    audioConfig,
    galleryItems,
    galleryOpen,
    viewerItems,
    viewerOpen,
    viewerIndex,
    locale,
  } = useUnit({
    sights: $sightsStore,
    messages: $assistantMessages,
    status: $assistantStatus,
    isBusy: $assistantIsBusy,
    thinkingText: $assistantThinkingText,
    filteredSuggestions: $assistantFilteredSuggestions,
    audioConfig: $audioConfig,
    galleryItems: $assistantGalleryItems,
    galleryOpen: $assistantGalleryOpen,
    viewerItems: $assistantViewerItems,
    viewerOpen: $assistantViewerOpen,
    viewerIndex: $assistantViewerIndex,
    locale: $locale,
  });
  const i18n = useI18n();
  const [
    submitPrompt,
    selectSuggestion,
    completeTextAnimation,
    openGallery,
    closeGallery,
    openViewer,
    closeViewer,
    setViewerIndex,
  ] = useUnit([
    assistantPromptSubmitted,
    assistantSuggestionSelected,
    assistantTextAnimationCompleted,
    assistantGalleryOpened,
    assistantGalleryClosed,
    assistantViewerOpened,
    assistantViewerClosed,
    assistantViewerIndexChanged,
  ]);
  const [addExternalMessage] = useUnit([assistantExternalMessageRequested]);
  const {
    isSpeaking,
    start: startPronouncing,
    stop: stopPronouncing,
  } = useAudioController();
  const [chatMode, setChatMode] = useState<AssistantChatMode>("hidden");
  const [requestedMode, setRequestedMode] = useState<AssistantChatMode | null>(
    null
  );
  const requestedModeRef = useRef(requestedMode);
  useEffect(() => {
    requestedModeRef.current = requestedMode;
  }, [requestedMode]);
  const [isSliding, setIsSliding] = useState(false);
  const [isVoiceSending, setIsVoiceSending] = useState(false);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const userScrolledRef = useRef(false);
  const lastSpokenMessageIdRef = useRef<string | null>(null);
  const lastMessageCountRef = useRef(0);
  const lastNonFullscreenModeRef = useRef<AssistantChatMode | null>(null);
  const lastAnimatedMessageIdRef = useRef<string | null>(null);
  const userScrollRetryTimeoutRef = useRef<number | null>(null);
  const [isAnimatingMessage, setIsAnimatingMessage] = useState(false);
  const lastStatusRef = useRef(status);
  const isKeyboardOpen = useMobileKeyboardOpen();
  const scrollHistoryToBottom = useCallback(() => {
    const history = historyRef.current;
    if (!history) {
      return;
    }
    if (userScrolledRef.current) {
      return;
    }
    history.scrollTop = history.scrollHeight;
  }, []);

  const handleHistoryScroll = useCallback(() => {
    const history = historyRef.current;
    if (!history) {
      return;
    }
    const threshold = 8;
    const distanceToBottom =
      history.scrollHeight - history.scrollTop - history.clientHeight;
    userScrolledRef.current = distanceToBottom > threshold;
  }, []);

  const handleTextAnimationComplete = useCallback(
    (messageId: string) => {
      completeTextAnimation(messageId);
      userScrolledRef.current = false;
      if (lastAnimatedMessageIdRef.current === messageId) {
        setIsAnimatingMessage(false);
      }
    },
    [completeTextAnimation]
  );

  const handleChatSubmit = useCallback(
    (message: string) => {
      if (chatMode === "hidden") {
        setRequestedMode("half-screen");
      }
      submitPrompt(message);
    },
    [chatMode, submitPrompt]
  );

  const handleSendVoiceMessage = useCallback(
    async (blob: Blob, mimeType: string) => {
      setIsVoiceSending(true);
      try {
        const payload = await requestSpeechToText(blob, {
          mimeType,
          languageCode: LOCALE_LANGUAGE_CODES[locale],
        });
        if (payload?.text) {
          if (chatMode === "hidden") {
            setRequestedMode("half-screen");
          }
          submitPrompt(payload.text);
        }
      } catch {
        addExternalMessage({
          text: i18n.assistant.voiceTranscriptionError,
        });
      } finally {
        setIsVoiceSending(false);
      }
    },
    [
      addExternalMessage,
      chatMode,
      i18n.assistant.voiceTranscriptionError,
      locale,
      submitPrompt,
    ]
  );

  const handleSuggestion = useCallback(
    (suggestion: AssistantSuggestion) => {
      stopPronouncing();
      if (
        suggestion.type === "orbit" &&
        (chatMode === "half-screen" || chatMode === "full-screen")
      ) {
        setRequestedMode("half-screen");
      }
      selectSuggestion(suggestion);
    },
    [chatMode, selectSuggestion, stopPronouncing]
  );

  const handleModeChange = useCallback(
    (mode: AssistantChatMode) => {
      setChatMode(mode);
      if (requestedModeRef.current && requestedModeRef.current === mode) {
        setRequestedMode(null);
      }
    },
    []
  );

  const handlePhotosClick = useCallback(
    (items: Array<Photo | Video>) => {
      if (!items.length) {
        return;
      }
      if (items.length === 1) {
        closeGallery();
        openViewer({ items, index: 0 });
        return;
      }
      openGallery(items);
    },
    [closeGallery, openGallery, openViewer]
  );

  const handleGalleryItemClick = useCallback(
    (_item: Photo | Video, index: number) => {
      openViewer({ items: galleryItems, index });
    },
    [galleryItems, openViewer]
  );

  const handleGalleryClose = useCallback(() => {
    closeGallery();
  }, [closeGallery]);

  useEffect(() => {
    scrollHistoryToBottom();
  }, [messages, scrollHistoryToBottom]);

  useEffect(() => {
    if (!isAnimatingMessage) {
      scrollHistoryToBottom();
    }
  }, [isAnimatingMessage, scrollHistoryToBottom]);

  useEffect(() => {
    const previousStatus = lastStatusRef.current;
    if (previousStatus !== status) {
      lastStatusRef.current = status;
      if (status === "awaiting" && !userScrolledRef.current) {
        scrollHistoryToBottom();
      }
    }
  }, [scrollHistoryToBottom, status]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== "user") {
      return;
    }
    userScrolledRef.current = false;
    scrollHistoryToBottom();
    if (userScrollRetryTimeoutRef.current !== null) {
      window.clearTimeout(userScrollRetryTimeoutRef.current);
    }
    userScrollRetryTimeoutRef.current = window.setTimeout(() => {
      userScrolledRef.current = false;
      scrollHistoryToBottom();
      userScrollRetryTimeoutRef.current = null;
    }, 32);
  }, [messages, scrollHistoryToBottom]);

  useEffect(() => {
    return () => {
      if (userScrollRetryTimeoutRef.current !== null) {
        window.clearTimeout(userScrollRetryTimeoutRef.current);
        userScrollRetryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isKeyboardOpen) {
      if (chatMode !== "full-screen") {
        lastNonFullscreenModeRef.current = chatMode;
        setRequestedMode("full-screen");
      }
      return;
    }

    if (
      lastNonFullscreenModeRef.current &&
      chatMode === "full-screen"
    ) {
      setRequestedMode(lastNonFullscreenModeRef.current);
    }
    lastNonFullscreenModeRef.current = null;
  }, [chatMode, isKeyboardOpen]);

  useEffect(() => {
    if (messages.length <= lastMessageCountRef.current) {
      lastMessageCountRef.current = messages.length;
      return;
    }

    lastMessageCountRef.current = messages.length;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) {
      return;
    }
    if (lastMessage.role === "assistant" && chatMode === "hidden") {
      setRequestedMode("half-screen");
      return;
    }
    if (lastMessage.role === "user" && chatMode === "hidden") {
      setRequestedMode("half-screen");
    }
  }, [chatMode, messages]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== "assistant") {
      return;
    }

    if (lastSpokenMessageIdRef.current === null) {
      lastSpokenMessageIdRef.current = lastMessage.id;
      return;
    }

    if (lastSpokenMessageIdRef.current === lastMessage.id) {
      return;
    }

    lastSpokenMessageIdRef.current = lastMessage.id;
    if (!audioConfig.soundOn || !audioConfig.voiceOn) {
      return;
    }

    const text = getAssistantText(lastMessage.blocks);

    if (text) {
      void startPronouncing(text);
    }
  }, [audioConfig.soundOn, audioConfig.voiceOn, messages, startPronouncing]);

  const latestAssistantMessage = useMemo(
    () => messages.findLast((message) => message.role === "assistant"),
    [messages]
  );
  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    if (!latestAssistantMessage) {
      setIsAnimatingMessage(false);
      lastAnimatedMessageIdRef.current = null;
      return;
    }

    if (latestAssistantMessage.id === lastAnimatedMessageIdRef.current) {
      return;
    }

    lastAnimatedMessageIdRef.current = latestAssistantMessage.id;
    setIsAnimatingMessage(!latestAssistantMessage.disableAnimation);
  }, [latestAssistantMessage]);

  const displayStatus = isVoiceSending ? "sending" : status;
  const effectiveStatus = isAnimatingMessage ? "idle" : displayStatus;
  // const lastMessage = messages.findLast(
  //   (message) => message.role === "assistant"
  // );

  return (
    <>
      {/* <div
        style={{
          position: "fixed",
          top: "50%",
          left: 0,
          right: 0,
          zIndex: 99999,
          background: "#000",
          color: "#0f0",
          font: "bold 13px/20px monospace",
          padding: "10px",
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        vvh:{Math.round(window.visualViewport?.height ?? 0)} iH:{window.innerHeight} kb:{isKeyboardOpen ? "Y" : "N"} m:{chatMode}
      </div> */}
      <AssistantChatPanel
        onModeChange={handleModeChange}
        onGestureChange={setIsSliding}
        requestedMode={requestedMode}
        footer={
          <ChatInput
            status={effectiveStatus}
            onSubmit={handleChatSubmit}
            onSendVoiceMessage={handleSendVoiceMessage}
            isSpeaking={isSpeaking}
            onStopSpeaking={stopPronouncing}
            placeholder={i18n.assistant.placeholder}
            disabled={isBusy}
          />
        }
      >
        <div
          ref={historyRef}
          className={styles.history}
          onScroll={handleHistoryScroll}
        >
          <div className={styles.historyIn}>
            {messages.map((message) => (
              <article
                key={message.id}
                className={cx(
                  styles.message,
                  message.role === "assistant"
                    ? styles.fromAssistant
                    : styles.fromUser
                )}
                aria-live={message.role === "assistant" ? "polite" : undefined}
              >
                <div className={styles.messageIn}>
                  {message.role === "assistant" ? (
                    <AssistantMessage
                      blocks={message.blocks}
                      disableAnimation={
                        message.disableAnimation ||
                        !(
                          latestAssistantMessage?.id != null &&
                          lastMessage?.id === latestAssistantMessage.id &&
                          message.id === latestAssistantMessage.id
                        )
                      }
                      onComplete={() => handleTextAnimationComplete(message.id)}
                      onUpdate={scrollHistoryToBottom}
                      onPhotosClick={handlePhotosClick}
                    />
                  ) : (
                    message.text
                  )}
                </div>
              </article>
            ))}
            {(effectiveStatus === "sending" ||
              effectiveStatus === "awaiting") && (
              <article
                className={cx(styles.message, styles.fromAssistant)}
                aria-live="polite"
              >
                <div className={styles.messageIn}>
                  <LoadingText>
                    {effectiveStatus === "sending"
                      ? i18n.assistant.status.sending
                      : thinkingText ?? i18n.assistant.status.thinking}
                  </LoadingText>
                </div>
              </article>
            )}
            {!isAnimatingMessage && (
              <Suggestions
                status={displayStatus}
                sights={sights}
                suggestions={filteredSuggestions}
                handleSuggestion={handleSuggestion}
              />
            )}
          </div>
        </div>

        {/* <div className={styles.lastMessage}>
          <div className={styles.lastMessageIn}>
            {displayStatus === "sending" || displayStatus === "awaiting" ? (
              <section
                className={cx(styles.message, styles.fromAssistant)}
                aria-live="polite"
              >
                <div className={styles.messageIn}>
                  <LoadingText>
                    {displayStatus === "sending"
                      ? i18n.assistant.status.sending
                      : i18n.assistant.status.thinking}
                  </LoadingText>
                </div>
              </section>
            ) : (
              lastMessage && (
                <article
                  key={lastMessage.id}
                  className={cx(styles.message, styles.fromAssistant)}
                  aria-live={
                    lastMessage.role === "assistant" ? "polite" : undefined
                  }
                >
                  <div className={styles.messageIn}>
                    <AssistantMessage
                      blocks={lastMessage.blocks}
                      disableAnimation={lastMessage.disableAnimation}
                      onComplete={() =>
                        handleTextAnimationComplete(lastMessage.id)
                      }
                      onUpdate={scrollHistoryToBottom}
                      onPhotosClick={handlePhotosClick}
                    />
                  </div>
                </article>
              )
            )}
          </div>
        </div> */}

      </AssistantChatPanel>
      <PhotoGallery
        open={galleryOpen}
        title={i18n.assistant.galleryTitle}
        items={galleryItems}
        onClick={handleGalleryItemClick}
        onClose={handleGalleryClose}
      />
      <PhotoViewer
        items={viewerItems}
        open={viewerOpen}
        index={viewerIndex}
        onClose={closeViewer}
        onIndexChange={setViewerIndex}
      />
    </>
  );
}
